import type { eventWithTime } from '@rrweb/types'
import type { TestSessionSnapshot } from './test-session-types'

export const MAX_REPLAY_TRANSFER_BYTES = 50 * 1024 * 1024

export type ReplayTransfer = {
  schemaVersion: 1
  createdAt: string
  session: TestSessionSnapshot
  replay: {
    events: eventWithTime[]
    stoppedByLimit: boolean
  }
}

type PackedReplayTransfer = Omit<ReplayTransfer, 'replay'> & {
  replay: {
    events: string[]
    stoppedByLimit: boolean
  }
}

function getUtf8ByteLength(text: string): number {
  return new TextEncoder().encode(text).byteLength
}

function assertTransferSize(text: string): void {
  if (getUtf8ByteLength(text) > MAX_REPLAY_TRANSFER_BYTES) {
    throw new Error('回放包超过 50 MiB，无法导入或导出')
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isPackedReplayTransfer(value: unknown): value is PackedReplayTransfer {
  if (!isRecord(value) || value.schemaVersion !== 1 || typeof value.createdAt !== 'string') return false
  if (!isRecord(value.session) || !Array.isArray(value.session.actions) || !Array.isArray(value.session.requests)) return false
  if (!isRecord(value.replay) || !Array.isArray(value.replay.events) || typeof value.replay.stoppedByLimit !== 'boolean') return false
  return value.replay.events.every((event) => typeof event === 'string')
}

function isReplayEvent(value: unknown): value is eventWithTime {
  return isRecord(value) && typeof value.type === 'number' && typeof value.timestamp === 'number'
}

export async function serializeReplayTransfer(input: Pick<ReplayTransfer, 'session' | 'replay'>): Promise<string> {
  const { pack } = await import('@rrweb/packer')
  const transfer: PackedReplayTransfer = {
    schemaVersion: 1,
    createdAt: new Date().toISOString(),
    session: input.session,
    replay: {
      events: input.replay.events.map((event) => pack(event)),
      stoppedByLimit: input.replay.stoppedByLimit,
    },
  }
  const text = JSON.stringify(transfer)
  assertTransferSize(text)
  return text
}

export async function parseReplayTransfer(text: string): Promise<ReplayTransfer> {
  assertTransferSize(text)
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw new Error('回放包不是有效的 JSON')
  }
  if (!isRecord(parsed) || parsed.schemaVersion !== 1) {
    throw new Error('不支持的回放包版本')
  }
  if (!isPackedReplayTransfer(parsed)) {
    throw new Error('回放包格式不完整')
  }
  const { unpack } = await import('@rrweb/packer')
  let events: eventWithTime[]
  try {
    events = parsed.replay.events.map((event) => unpack(event))
  } catch {
    throw new Error('回放包事件无法解压')
  }
  if (!events.every(isReplayEvent)) {
    throw new Error('回放包事件格式无效')
  }
  return {
    schemaVersion: 1,
    createdAt: parsed.createdAt,
    session: parsed.session,
    replay: { events, stoppedByLimit: parsed.replay.stoppedByLimit },
  }
}
